import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, Share2, Check } from "lucide-react";

/**
 * Referral card — shareable code + Web Share API + invite stats.
 * Designed for HAEVEN: parchment/coral palette, plays with Settings page tone.
 */
export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/referrals/me").then((r) => { if (alive) setData(r.data); })
      .catch((err) => console.warn("Failed to load referral info", err));
    return () => { alive = false; };
  }, []);

  const copy = async () => {
    if (!data?.code) return;
    try {
      await navigator.clipboard.writeText(data.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Code copied");
    } catch { toast.error("Could not copy"); }
  };

  const share = async () => {
    if (!data?.share) return;
    if (navigator.share) {
      try { await navigator.share(data.share); }
      catch (err) { if (err?.name !== "AbortError") console.warn("Share failed", err); }
    } else {
      try {
        await navigator.clipboard.writeText(`${data.share.text} ${data.share.url}`);
        toast.success("Invite copied — paste it anywhere");
      } catch { toast.error("Could not copy"); }
    }
  };

  return (
    <section className="brutal-card bg-card p-5" data-testid="settings-referrals">
      <div className="flex items-center gap-2 mb-2">
        <Gift size={18} strokeWidth={2.5} />
        <h2 className="text-xl">Invite a friend</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        When a friend signs up with your code, you BOTH get <strong>{data?.bonus_days ?? 30} free days</strong> of HAEVEN Calm.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <code
          data-testid="referral-code"
          className="brutal-input px-4 py-2 text-lg tracking-widest font-bold bg-background select-all"
        >
          {data?.code || "······"}
        </code>
        <button
          onClick={copy}
          data-testid="referral-copy"
          disabled={!data?.code}
          className="brutal-btn px-3 py-2 bg-card text-sm inline-flex items-center gap-1 disabled:opacity-50"
        >
          {copied ? <Check size={14} strokeWidth={3} /> : <Copy size={14} strokeWidth={2.5} />}
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          onClick={share}
          data-testid="referral-share"
          disabled={!data?.code}
          className="brutal-btn px-3 py-2 bg-accent text-sm inline-flex items-center gap-1 disabled:opacity-50"
        >
          <Share2 size={14} strokeWidth={2.5} /> Share
        </button>
      </div>
      {data?.stats?.invites_sent > 0 && (
        <p className="text-xs text-muted-foreground mt-3" data-testid="referral-stats">
          You&apos;ve invited <strong>{data.stats.invites_sent}</strong> friend{data.stats.invites_sent === 1 ? "" : "s"} · earned <strong>{data.stats.bonus_days_earned} days</strong> of Calm.
        </p>
      )}
    </section>
  );
}
